import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import '../common.i18n';
import { Button, Chip, Segmented, Skeleton, StateView, useToast } from '@hana/ui';
import { StateCard, meeStateProps } from '../meeState';
import {
  IconCalendarStats, IconCash, IconChevronDown, IconChevronRight, IconChecklist, IconDownload, IconEye, IconEyeOff, IconFileText,
  IconHistory, IconInfoCircle, IconLink, IconList, IconMessage, IconRoute, IconUsers, IconChartBar,
} from '@tabler/icons-react';
import { MeeHeaderTools } from '../Parts';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import p from './pay.module.css';
import { num } from '../home/view';
import { MASK, PAY_KEYS, periodWork, slipOf, vnd, type SlipLine } from './slip';

const INCOME_COLORS = ['var(--d-blue)', 'var(--d-green)', 'var(--d-violet)', 'var(--d-amber)'];
const DEDUCT_COLORS = ['var(--err-bd)', 'var(--err-bg)', 'var(--line)', 'var(--err)'];
/** tự che lại sau 30 giây (board: togPay) */
const REHIDE_MS = 30_000;

type Sec = 'sum' | 'b0' | 'b1' | 'work' | 'act' | 'todo' | 'info' | 'steps' | 'ppl' | 'hist' | 'links';
/** mặc định mở / gập theo board (d21Kit acc) */
const OPEN0: Record<Sec, boolean> = { sum: true, b0: true, b1: true, work: true, act: false, todo: true, info: true, steps: false, ppl: true, hist: true, links: false };

function Fold({ title, icon, tip, open, onToggle, aria, region, extra, children }: { region?: string; title: string; icon: ReactNode; tip: string; open: boolean; onToggle: () => void; aria?: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={region ?? title} className={p.card}>
      <div className={p.ch}>
        <span className={p.ico} aria-hidden="true">{icon}</span>
        <h2 className={p.h2}>{title}</h2>
        <span className={p.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {extra}
        <button type="button" className={p.fold} aria-expanded={open} aria-label={`Mở hoặc gập ${aria ?? title}`} onClick={onToggle} data-open={open ? 1 : undefined}>
          <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
        </button>
      </div>
      {open && <div className={p.cb}>{children}</div>}
    </section>
  );
}

function Av({ u }: { u: string }) {
  return <span className={p.av} aria-hidden="true" title={u}>{u.slice(0, 2).toUpperCase()}</span>;
}

function Block({ title, tip, icon, rows, total, colors, show, open, onToggle }: { title: string; tip: string; icon: ReactNode; rows: SlipLine[]; total: number; colors: string[]; show: boolean; open: boolean; onToggle: () => void }) {
  return (
    <Fold title={title} aria="mục" tip={tip} icon={icon} open={open} onToggle={onToggle} extra={<b className={p.tot}>{show ? vnd(total) : MASK}</b>}>
      <div className={s.mix} role="img" aria-label={show ? `${title} ${vnd(total)}` : `${title} · đang che`}>
        {rows.map((r, i) => (
          <i key={r.k} style={{ flex: show ? r.v : 1, background: show ? colors[i % colors.length] : 'var(--ctl)' }} />
        ))}
      </div>
      <div className={s.slipRows}>
        {rows.map((r, i) => (
          <div key={r.k}>
            <i className={s.sq} style={{ background: show ? colors[i % colors.length] : 'var(--ctl)' }} />
            <span>{r.k}</span>
            <b>{show ? vnd(r.v) : MASK}</b>
          </div>
        ))}
      </div>
    </Fold>
  );
}

function PayBody({ m, per, setPer, show, toggle }: { m: ReturnType<typeof useMee>; per: string; setPer: (k: string) => void; show: boolean; toggle: () => void }) {
  const t = useT();
  const toast = useToast();
  const nav = useNavigate();
  const me = m.me!;
  const cur = slipOf(me, per);
  const work = periodWork(m.cal, per, cur.hasOt);
  const hist = [...PAY_KEYS].reverse().map((k) => ({ k, net: slipOf(me, k).net }));
  const top = Math.max(...hist.map((h) => h.net));
  const pdf = (k: string) => toast({ msg: t('mc.pdfToast', { p: k }) });
  const [open, setOpen] = useState(OPEN0);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const tg = (k: Sec) => () => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const seat = me.seats[0];
  const todo = [
    { id: 'ask', k: `Thắc mắc phiếu kỳ ${per} (nếu có)`, who: 'HANHLTM', due: 'Hạn 08/Oct', actK: 'Gửi', act: () => toast({ msg: `Đã gửi thắc mắc phiếu ${per} tới [HRD-MGR] HANHLTM` }) },
    { id: 'pdf', k: `Tải PDF phiếu kỳ ${per}`, who: me.id, due: 'có mật khẩu', actK: 'Tải', act: () => pdf(per) },
  ];
  const steps = [
    { seat: '[HRD-MGR · kiêm]', who: 'HANHLTM', st: 'Xong', sub: 'Chốt công · tính lương' },
    { seat: '[ACC-CA] trống → [BOD-DIR]', who: 'PHILIP', st: 'Xong', sub: 'Kế toán trưởng kiểm' },
    { seat: '[BOD-DIR]', who: 'PHILIP', st: 'Xong', sub: 'Giám đốc duyệt' },
    { seat: '[ACC-CP]', who: 'HANHLTM', st: 'Đã chi', sub: `Chi ${cur.paid}` },
  ];
  const acts = [
    { k: `Phiếu kỳ ${per} gửi vào mail công ty`, who: 'Hệ thống', when: cur.paid, kind: 'Gửi' },
    { k: `Bảng lương kỳ ${per} được duyệt`, who: 'PHILIP', when: cur.paid, kind: 'Duyệt' },
  ];
  const ppl: Array<[string, string]> = [['HANHLTM', '[HRD-MGR · kiêm] nhận thắc mắc phiếu'], ['PHILIP', '[BOD-DIR] duyệt bảng lương']];
  const openT = todo.filter((x) => !done[x.id]).length;

  const left = (
    <div className={s.col}>
      <Fold title={`Tóm tắt · kỳ ${per}`} aria="Tóm tắt · kỳ" region="Tóm tắt phiếu lương" tip="Chỉ chính chủ xem. Số che mặc định, bấm Hiện để xem và tự che lại sau 30 giây. Số minh hoạ." icon={<IconCash size={18} stroke={1.9} />} open={open.sum} onToggle={tg('sum')}>
        <div className={s.slipNet}>
          <div>
            <span>Thực nhận · kỳ {per}</span>
            <b>{show ? vnd(cur.net) : MASK}</b>
          </div>
          <Chip tone="ok">Đã chi {cur.paid}</Chip>
          <Button icon={show ? <IconEyeOff size={18} /> : <IconEye size={18} />} aria-pressed={show} onClick={toggle}>
            {show ? 'Che' : 'Hiện'}
          </Button>
        </div>
      </Fold>
      <Block title="Thu nhập" tip="Lương theo ngày công, phụ cấp và làm thêm giờ của kỳ." icon={<IconCash size={18} stroke={1.9} />} rows={cur.income} total={cur.totalIncome} colors={INCOME_COLORS} show={show} open={open.b0} onToggle={tg('b0')} />
      <Block title="Khấu trừ" tip="Bảo hiểm phần người lao động (8% · 1,5% · 1%) và thuế TNCN lũy tiến." icon={<IconList size={18} stroke={1.9} />} rows={cur.deduct} total={cur.totalDeduct} colors={DEDUCT_COLORS} show={show} open={open.b1} onToggle={tg('b1')} />
      <Fold title={t('mc.workLeave')} tip={t('mc.workLeaveTip')} icon={<IconCalendarStats size={18} stroke={1.9} />} open={open.work} onToggle={tg('work')}>
        <div className={s.meta}>
          <Chip label="WorkCalendar.standardDaysInMonth">{t('mc.std')} <b>{num(work.std)}</b></Chip>
          <Chip label={t('mc.locked')}>{t('mc.actual')} <b>{num(work.actual)}</b></Chip>
          <Chip label="PaidLeaveDays">{t('mc.paid')} <b>{work.paidLeave}</b></Chip>
          <Chip label={t('mc.otLabel')}>{t('mc.ot')} <b>{show ? `${num(work.otHours)} h` : '••'}</b></Chip>
        </div>
      </Fold>
      <Fold title="Hoạt động" tip="Nhật ký phiếu của bạn: phiếu gửi qua email, bạn xem / tải PDF, thắc mắc đã gửi." icon={<IconHistory size={18} stroke={1.9} />} open={open.act} onToggle={tg('act')}>
        {acts.map((a) => (
          <div key={a.k} className={p.row}>
            <Av u={a.who} />
            <span className={p.main}><b>{a.k}</b><span>{a.who} · {a.when}</span></span>
            <Chip>{a.kind}</Chip>
          </div>
        ))}
      </Fold>
    </div>
  );

  const right = (
    <div className={s.col}>
      <Fold title="Việc cần làm" tip="Việc với phiếu này: thắc mắc phiếu gửi ghế HRD-MGR trong 3 ngày làm việc sau ngày chi." icon={<IconChecklist size={18} stroke={1.9} />} open={open.todo} onToggle={tg('todo')} extra={openT > 0 ? <Chip tone="warn">{openT}</Chip> : undefined}>
        {todo.map((x) => (
          <div key={x.id} className={p.row} data-done={done[x.id] ? 1 : undefined}>
            <button type="button" className={p.chk} aria-pressed={!!done[x.id]} aria-label={`Xong: ${x.k}`} onClick={() => setDone((d) => ({ ...d, [x.id]: !d[x.id] }))}>{done[x.id] ? '✓' : ''}</button>
            <span className={p.main}><b>{x.k}</b><span>{x.who} · {x.due}</span></span>
            {!done[x.id] && <Button size="sm" onClick={x.act}>{x.actK}</Button>}
          </div>
        ))}
      </Fold>
      <Fold title="Thông tin chính" tip="Kỳ, ngày chi, ghế và phòng của bạn." icon={<IconFileText size={18} stroke={1.9} />} open={open.info} onToggle={tg('info')}>
        {([['Ghế', seat ? `[${seat[0]}] ${seat[1]}` : '—'], ['Phòng', me.dept]] as const).map(([k, v]) => (
          <div key={k} className={p.kv}><span>{k}</span><b>{v}</b></div>
        ))}
      </Fold>
      <Fold title="Các bước duyệt theo ghế" tip="Phiếu đi qua: HC–NS chốt công và tính → Kế toán trưởng kiểm (ghế ACC-CA, trống thì leo lên BOD-DIR) → Giám đốc duyệt → Kế toán chi." icon={<IconRoute size={18} stroke={1.9} />} open={open.steps} onToggle={tg('steps')} extra={<Chip tone="ok">{steps.length}/{steps.length}</Chip>}>
        {steps.map((x, i) => (
          <div key={i} className={p.row}>
            <span className={p.step} aria-hidden="true">✓</span>
            <span className={p.main}><b>{x.seat} {x.who}</b><span>{x.sub}</span></span>
            <Chip tone="ok">{x.st}</Chip>
          </div>
        ))}
      </Fold>
      <Fold title="Người phụ trách" tip="Avatar lấy từ TblStaff.ImageProfile (HanaAvatar), chưa có ảnh thì hiện chữ cái. Người duyệt hiện theo ghế (TblOrgAssign)." icon={<IconUsers size={18} stroke={1.9} />} open={open.ppl} onToggle={tg('ppl')}>
        {ppl.map(([u, role]) => (
          <div key={u} className={p.row}>
            <Av u={u} />
            <span className={p.main}><b>{u}</b><span>{role}</span></span>
            <Button size="sm" variant="ghost" icon={<IconMessage size={16} />} aria-label={`Nhắn ${u} trong HANA Hei`} onClick={() => toast({ msg: `Mở HANA Hei · ${u}` })} />
          </div>
        ))}
      </Fold>
      <Fold title="6 kỳ gần nhất" tip="Bấm một kỳ để xem phiếu kỳ đó. Tải PDF có mật khẩu." icon={<IconChartBar size={18} stroke={1.9} />} open={open.hist} onToggle={tg('hist')}>
        <div className={s.months} role="img" aria-label={show ? 'Thực nhận 6 kỳ gần nhất' : 'Đang che số'}>
          {hist.map((h) => (
            <div key={h.k} data-on={h.k === per ? 1 : undefined} style={{ height: show ? `${Math.round((h.net / top) * 100)}%` : '40%', background: show ? undefined : 'var(--ctl)' }} />
          ))}
        </div>
        <div className={s.monthLb}>
          {hist.map((h) => (
            <span key={h.k} style={{ color: h.k === per ? 'var(--tx)' : undefined }}>{h.k.slice(0, 2)}</span>
          ))}
        </div>
        {PAY_KEYS.slice(0, 3).map((k) => (
          <div key={k} className={p.row}>
            <button type="button" className={p.pick} aria-pressed={k === per} onClick={() => setPer(k)}>Kỳ {k}</button>
            <span className={p.paid}>{slipOf(me, k).paid.slice(0, 6)}</span>
            <Button size="sm" variant="ghost" icon={<IconDownload size={16} />} aria-label={`Tải PDF phiếu lương ${k}`} onClick={() => pdf(k)} />
          </div>
        ))}
      </Fold>
      <Fold title="Liên kết" tip="Lối sang màn liên quan. Mở trong cùng HANA Ezy, giữ ngữ cảnh bản ghi." icon={<IconLink size={18} stroke={1.9} />} open={open.links} onToggle={tg('links')}>
        {([['Hồ sơ & giấy tờ', 'Hợp đồng, thông tin của tôi', '/mee/docs'], ['Nghỉ phép & đơn', 'Phép năm còn lại', '/mee/leave']] as const).map(([k, sub, to]) => (
          <button key={k} type="button" className={p.link} onClick={() => void nav({ to })}>
            <IconLink size={16} stroke={1.9} aria-hidden="true" />
            <span className={p.main}><b>{k}</b><span>{sub}</span></span>
            <IconChevronRight size={16} aria-hidden="true" />
          </button>
        ))}
      </Fold>
    </div>
  );

  return <div className={`${s.page} ${p.grid}`}>{left}{right}</div>;
}

export function PayScreen() {
  const t = useT();
  const m = useMee();
  const toast = useToast();
  const nav = useNavigate();
  const [per, setPer] = useState(PAY_KEYS[0]!);
  const [shown, setShown] = useState(false);
  const show = shown && m.state === 'ready';
  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown(false), REHIDE_MS);
    return () => clearTimeout(t);
  }, [shown]);
  return (
    <>
      <PageHeader
        title="Lương"
        tabs={
          m.state === 'ready' ? (
            <Segmented label="Kỳ lương" value={per} onChange={setPer} items={PAY_KEYS.slice(0, 3).map((k) => ({ key: k, label: k }))} />
          ) : undefined
        }
        actions={
          m.state === 'ready' ? (
            <>
              <MeeHeaderTools />
              <Button variant="primary" icon={<IconDownload size={18} />} onClick={() => toast({ msg: t('mc.pdfToast', { p: per }) })}>
                {t('mc.pdf')}
              </Button>
            </>
          ) : undefined
        }
      />
      <Page kind="table">
        <StateCard state={m.state}>
          <StateView
            state={m.state}
            {...meeStateProps('pay', m.state, m.retry, () => nav({ to: '/mee/time' }))}
            skeleton={<div className={s.skel}><Skeleton rows={3} /><Skeleton rows={5} /></div>}
          >
            <PayBody m={m} per={per} setPer={setPer} show={show} toggle={() => setShown((v) => !v)} />
          </StateView>
        </StateCard>
      </Page>
    </>
  );
}
